import { useEffect, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { LockClosedIcon } from '@heroicons/react/24/outline'

interface InputProps {
  label: string
  placeholder?: string
  measures: { key: string; value: string }[]
  showInline?: boolean
  disabled?: boolean
  disabledMeasure?: boolean
  disabledValue?: boolean
  valueDefault?: { quantity: number; measure: string }
  value?: number
  maxDigits?: number
}

const MeasureInput = (props: UseControllerProps & InputProps) => {
  const {
    label,
    placeholder,
    measures,
    showInline,
    disabled,
    disabledMeasure,
    disabledValue,
    valueDefault,
    value,
    maxDigits,
  } = props
  const { field, fieldState } = useController(props)

  const [input, setInput] = useState<{
    quantity?: number
    measure?: string
  }>(valueDefault ?? { quantity: 0, measure: measures[0].key })

  useEffect(() => {
    field.onChange({ ...input })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])

  useEffect(() => {
    if (value)
      setInput((prev) => {
        return {
          ...prev,
          quantity: value,
        }
      })
  }, [value])
  return (
    <div
      className={`${showInline ? 'inline-flex items-center gap-3' : 'flex-col'}`}
    >
      <label
        className={`block text-sm font-medium ${disabled || disabledValue ? 'text-gray-400' : 'text-gray-700'}`}
        htmlFor={label}
      >
        <span className="inline-flex items-center">
          {label}
          {(disabled || disabledValue) && (
            <LockClosedIcon className="h-4 px-2" />
          )}
        </span>
      </label>
      <div className="relative mt-1 rounded-md shadow-sm">
        <input
          className={`${
            fieldState.error
              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
              : `focus:border-gray-600 focus:ring-gray-500 ${
                  disabled || disabledValue
                    ? 'border-gray-300 text-gray-400'
                    : 'border-gray-500 text-gray-500'
                }`
          } block w-full rounded-md pr-10 placeholder:text-slate-400 sm:text-sm`}
          disabled={disabled || disabledValue}
          min={0}
          onChange={(e) => {
            const splitted = e.target.value.split('.')
            if (
              maxDigits &&
              splitted.length > 1 &&
              splitted[1].length > maxDigits
            )
              return
            setInput({
              ...input,
              quantity:
                e.target.value === '' ? undefined : Number(e.target.value),
            })
          }}
          placeholder={placeholder && placeholder}
          step={maxDigits ? 1 / Math.pow(10, maxDigits) : undefined}
          type="number"
          value={input.quantity?.toString() ? input.quantity : ''}
        />
        <div className="absolute right-0 top-0 flex items-center gap-1 pr-0">
          {fieldState.error && (
            <ExclamationCircleIcon
              aria-hidden="true"
              className="h-5 w-5 text-red-500"
            />
          )}
          <select
            className={[
              'rounded-md rounded-l-none',
              'border-transparent border-l-gray-700',
              'bg-transparent py-2 pl-2 pr-7',
              'text-gray-500 focus:border-slate-500 focus:ring-slate-500',
              'sm:text-sm',
            ].join(' ')}
            disabled={disabled || disabledMeasure}
            onChange={(e) => {
              setInput({ ...input, measure: e.target.value })
            }}
            value={input.measure}
          >
            {measures.map((item, idx) => (
              <option
                dangerouslySetInnerHTML={{ __html: item.value }}
                key={idx}
                value={item.key}
              />
            ))}
          </select>
        </div>
        {fieldState.error && (
          <p className="absolute mt-1 text-xs text-red-600">
            {fieldState.error?.message}
          </p>
        )}
      </div>
    </div>
  )
}

export default MeasureInput
